import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  downloadLateRpm,
  getCommunicationLogs,
  upsertCommunicationLog,
  deleteCommunicationLog,
  weightScale,
} from "app/services/patient/rpmPatient";
import {
  fetchPatientAnalytics,
  fetchPatientBpReadings,
  sendLetter,
} from "app/services/transmission/patient";
import {
  downloadBillingHistory,
  fetchDeviceAlertList,
  fetchPatientNotesHistory,
  fetchSubscriptionPauseHistory,
  formSettings,
  getDisconnectedMonitors,
  getPatients,
  getReminderPatients,
  patientDataPoints,
  storePatientDataPoint,
  updatePatientAdvisory,
  updatePatientById,
  updateSelfPayStatus,
  updatePreAuthStatus,
  patientNoteRequestToDelete,
} from "app/services/patient/patient";

import {
  addLiveCall,
  downloadOverduePatients,
  enrollNonEnrolledPatient,
  getNonCompliantPatients,
  getNonEnrolledPatients,
  getPatientReminderHistory,
  updateLiveCall,
} from "app/services/patient/nonCompliantPatients";
import { BASE_URL } from "static/constants";
import { downloadFile } from "app/utils/file";
import { getFormattedDateTime } from "utils/date";
import { showErrorToast } from "utils/toast";

export const patientApi = createApi({
  reducerPath: "patientApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: [
    "patientList",
    "nonCompliantPatients",
    "nonEnrolledPatients",
    "fetchCommunicationNotes",
    "patientReminderHistory",
    "patientNotesHistory",
    "subscriptionPauseHistory",
  ],
  endpoints: ({ query, mutation }) => ({
    getPatientList: query({
      queryFn: ({
        page,
        pageSize,
        search,
        filters,
        sorting,
        showPatientAlert,
        showDeviceAlert,
        showMissingContactInfo,
        showAlertWhenDisconnected,
      }) => {
        return {
          data: getPatients({
            page,
            pageSize,
            search,
            filters,
            sorting,
            showPatientAlert,
            showDeviceAlert,
            showMissingContactInfo,
            showAlertWhenDisconnected,
          }),
        };
      },
      providesTags: ["patientList"],
    }),
    getCommunicationLogs: query({
      queryFn: ({ patientId, fetch }) => {
        return {
          data: getCommunicationLogs(patientId),
        };
      },
    }),
    saveCommunicationLog: mutation({
      queryFn: ({ patientId, communicationLog, isEditAction }) => {
        return {
          data: upsertCommunicationLog(
            patientId,
            communicationLog,
            isEditAction
          ),
        };
      },
    }),
    deleteCommunicationLog: mutation({
      queryFn: (id) => {
        return {
          data: deleteCommunicationLog(id),
        };
      },
    }),
    downloadRpmList: mutation({
      queryFn: (account) => {
        return downloadLateRpm(account)
          .then((response) => {
            const fileName = `late-rpm-list-${getFormattedDateTime()}.csv`;

            const file = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const fileURL = URL.createObjectURL(file);
            let a = document.createElement("a");

            document.body.appendChild(a);
            a.style = "display:none";

            a.href = fileURL;
            a.download = fileName;
            a.click();

            a.remove();
            return { data: response };
          })
          .catch((error) => {
            showErrorToast(error?.message);
          });
      },
    }),
    getPatientAnalytics: query({
      queryFn: ({ patientId }) => {
        return {
          data: fetchPatientAnalytics(patientId),
        };
      },
    }),
    getRpmAnalytics: query({
      queryFn: ({ patientId, filter }) => {
        return {
          data: fetchPatientBpReadings(patientId, filter),
        };
      },
    }),
    getDeviceAlertList: query({
      queryFn: (filter = {}) => {
        return {
          data: fetchDeviceAlertList(filter),
        };
      },
    }),
    updatePatient: mutation({
      queryFn: ({ patientId, payload }) => {
        return {
          data: updatePatientById(patientId, payload),
        };
      },
      invalidatesTags: ["patientList"],
    }),
    downloadPatientList: mutation({
      queryFn: (query) => {
        return getPatients(query, true).then(async (response) => {
          const fileName = `patients-${getFormattedDateTime()}.xlsx`;

          await downloadFile(response, fileName);

          return { data: response };
        });
      },
    }),
    getFormSettings: query({
      queryFn: () => {
        return {
          data: formSettings(),
        };
      },
    }),
    getPatientDataPoints: query({
      queryFn: (patientId) => {
        return {
          data: patientDataPoints(patientId),
        };
      },
    }),
    updatePatientDataPoint: mutation({
      queryFn: ({ patientId, formData, isUpdate }) => {
        return {
          data: storePatientDataPoint(patientId, formData, isUpdate),
        };
      },
    }),
    downloadDisconnectedMonitorsList: mutation({
      queryFn: async ({ payload, downloadType }) => {
        const response = await getDisconnectedMonitors(payload, downloadType);
        return { data: response };
      },
    }),
    sendLetterToPatient: mutation({
      queryFn: async ({ serialNumber, filePath, documentId }) => {
        const response = await sendLetter(serialNumber, filePath, documentId);
        return { data: response };
      },
    }),

    getWeightScale: query({
      queryFn: (patientId) => {
        return {
          data: weightScale(patientId),
        };
      },
    }),
    downloadBillingHistory: mutation({
      queryFn: async (payload) => {
        const response = await downloadBillingHistory(payload);
        return { data: response };
      },
    }),
    downloadReminderPatientList: mutation({
      queryFn: (values) => {
        return getReminderPatients(values).then(async (response) => {
          const fileName = `patients-${getFormattedDateTime()}.xlsx`;

          await downloadFile(response, fileName);

          return { data: response };
        });
      },
    }),
    getNonCompliantPatients: query({
      queryFn: ({
        year,
        month,
        account,
        scheduleType,
        noShowType,
        page,
        pageSize,
        filters,
        sorting,
        subscription,
      }) => {
        return {
          data: getNonCompliantPatients({
            year,
            month,
            account,
            scheduleType,
            noShowType,
            page,
            pageSize,
            filters,
            sorting,
            subscription,
          }),
        };
      },
      providesTags: ["nonCompliantPatients"],
    }),
    getPatientReminderHistory: query({
      queryFn: ({ patientId }) => {
        return {
          data: getPatientReminderHistory(patientId),
        };
      },
      providesTags: ["patientReminderHistory"],
    }),
    getPatientNotesHistory: query({
      queryFn: ({ patientId }) => {
        return {
          data: fetchPatientNotesHistory(patientId),
        };
      },
      providesTags: ["patientNotesHistory"],
    }),
    getSubscriptionPauseHistory: query({
      queryFn: async ({ patientId }) => {
        try {
          return { data: await fetchSubscriptionPauseHistory(patientId) };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["subscriptionPauseHistory"],
    }),
    getNonEnrolledPatients: query({
      queryFn: ({ page, pageSize, filters, sorting }) => {
        return {
          data: getNonEnrolledPatients({ page, pageSize, filters, sorting }),
        };
      },
      providesTags: ["nonEnrolledPatients"],
    }),
    downloadOverduePatientList: mutation({
      queryFn: async (query) => {
        try {
          const response = await downloadOverduePatients(query);
          const fileName = `Overdue-Patients-${getFormattedDateTime()}.xlsx`;

          const file = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const fileURL = URL.createObjectURL(file);
          let a = document.createElement("a");

          document.body.appendChild(a);
          a.style = "display:none";

          a.href = fileURL;
          a.download = fileName;
          a.click();

          a.remove();
          return { data: response };
        } catch (error) {
          showErrorToast(error?.message);
        }
      },
    }),
    enrollNonEnrolledPatient: mutation({
      queryFn: (payload) => {
        return { data: enrollNonEnrolledPatient(payload) };
      },
      invalidatesTags: ["nonEnrolledPatients", "nonCompliantPatients"],
    }),
    addLiveCall: mutation({
      queryFn: (payload) => {
        return { data: addLiveCall(payload) };
      },
    }),
    updateLiveCall: mutation({
      queryFn: (payload) => {
        return { data: updateLiveCall(payload) };
      },
    }),
    updatePatientAdvisory: mutation({
      queryFn: (payload) => {
        return { data: updatePatientAdvisory(payload) };
      },
      invalidatesTags: ["patientList"],
    }),
    updateSelfPayStatus: mutation({
      queryFn: ({ patientId, selfPay }) => {
        return { data: updateSelfPayStatus(patientId, selfPay) };
      },
    }),
    updatePreAuthStatus: mutation({
      queryFn: ({ patientId, preAuth }) => {
        return { data: updatePreAuthStatus(patientId, preAuth) };
      },
    }),
    patientNoteRequestToDelete: mutation({
      queryFn: (payload) => {
        return {
          data: patientNoteRequestToDelete(payload),
        };
      },
      invalidatesTags: ["patientNotesHistory"],
    }),
  }),
});

export const {
  useGetWeightScaleQuery,
  useGetPatientListQuery,
  useAddLiveCallMutation,
  useGetRpmAnalyticsQuery,
  useGetFormSettingsQuery,
  useUpdatePatientMutation,
  useUpdateLiveCallMutation,
  useDownloadRpmListMutation,
  useGetDeviceAlertListQuery,
  useGetPatientAnalyticsQuery,
  useGetCommunicationLogsQuery,
  useGetPatientDataPointsQuery,
  useDownloadPatientListMutation,
  useSendLetterToPatientMutation,
  useGetPatientNotesHistoryQuery,
  useGetSubscriptionPauseHistoryQuery,
  useGetNonEnrolledPatientsQuery,
  useSaveCommunicationLogMutation,
  useGetNonCompliantPatientsQuery,
  useDeleteCommunicationLogMutation,
  useUpdatePatientDataPointMutation,
  useDownloadBillingHistoryMutation,
  useGetPatientReminderHistoryQuery,
  useEnrollNonEnrolledPatientMutation,
  useDownloadReminderPatientListMutation,
  useDownloadDisconnectedMonitorsListMutation,

  useFetchCommunicationNotesQuery,
  useAddCommunicationNotesMutation,
  useDownloadThirdReminderPatientListMutation,
  useDownloadOverduePatientListMutation,
  useUpdatePatientAdvisoryMutation,
  useUpdateSelfPayStatusMutation,
  useUpdatePreAuthStatusMutation,
  usePatientNoteRequestToDeleteMutation,
} = patientApi;
